/**
 * Decides whether a Sentry event is worth reporting.
 *
 * Chrome and the Google app on iOS inject their own scripts into the page. When those
 * throw, the error reaches our global handlers as though it were ours, and the stack
 * carries no frame from our bundle: WebKit attributes the throw either to `[native code]`
 * or to the document URL itself, using the injected script's own line numbers -- 415:45
 * against a document that is 53 lines long.
 *
 * Enumerating those markers has already failed once. 44eab86 dropped only frames with no
 * filename at all, and both live signatures carry one, so the noise kept arriving for a
 * week (sip-3mb). This asks the question the other way round instead: did *any* frame come
 * from code we shipped? Everything we serve lives under `/_app/`, so a stack with nothing
 * under that prefix is not ours, whatever marker the next in-app browser invents.
 *
 * Only unhandled events are judged this way. Anything we handed to `captureException`
 * ourselves is a deliberate report and is kept whatever its stack looks like.
 *
 * @param {{exception?: {values?: Array<{mechanism?: {handled?: boolean}, stacktrace?: {frames?: Array<{filename?: string}>}}>}}} event
 * @returns {boolean}
 */
export function shouldReport(event) {
	const values = event.exception?.values ?? [];
	if (!values.some((v) => v.mechanism?.handled === false)) {
		return true;
	}

	// No frames at all means there is nothing to judge -- a cross-origin "Script error."
	// looks like this -- so leave those alone rather than widening the drop by accident.
	const frames = values.flatMap((v) => v.stacktrace?.frames ?? []);
	if (frames.length === 0) {
		return true;
	}

	return frames.some((f) => f.filename?.includes('/_app/'));
}
